/**
 * One stage of the triage workspace as a collapsible section.
 *
 * States:
 * - `locked`    — greyed, not expandable; shows the unlock condition.
 * - `active`    — the request's current stage; auto-expanded by the page.
 * - `completed` — collapsed to a DATA summary (never a bare header) with
 *                 a guarded "Change" that opens the revert modal
 *                 pre-targeted at this section's stage.
 * - `record`    — read-only record view (started/rejected): collapsed
 *                 summary, expandable, no Change.
 *
 * Children are lazy-mounted: heavy section bodies render only once the
 * section has been expanded. Collapsing returns focus to the section
 * header (APG disclosure pattern).
 */

import { ReactNode, useEffect, useRef, useState } from 'react';

import { Badge, Button, Card, Collapse, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconLock } from '@tabler/icons-react';

import classes from './TriageWorkspace.module.css';

export type StageSectionState = 'locked' | 'active' | 'completed' | 'record';

interface StageSectionProps {
  /** Section slug — used for element ids / testids and deep-link
   * targets. Any workspace's slug vocabulary works (triage, intake). */
  slug: string;
  /** 1-based display number — derived from the effective section list,
   * so legacy flows number correctly (no hard-coded "4."). */
  index: number;
  label: string;
  state: StageSectionState;
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
  /** Data summary shown while collapsed (completed/record states). */
  summary?: ReactNode;
  /** Unlock condition line for locked sections. */
  lockedHint?: string;
  /** "Change" affordance — opens the revert modal pre-targeted. */
  onChange?: () => void;
  /** Increment to flash-highlight the section (blocker chip jump). */
  flashKey?: number;
  children: ReactNode;
}

const STATE_BADGE: Record<StageSectionState, { label: string; color: string } | null> = {
  locked: { label: 'Cannot start yet', color: 'gray' },
  active: { label: 'In progress', color: 'blue' },
  completed: { label: 'Completed', color: 'green' },
  record: null,
};

export function StageSection({
  slug,
  index,
  label,
  state,
  expanded,
  onToggle,
  summary,
  lockedHint,
  onChange,
  flashKey = 0,
  children,
}: Readonly<StageSectionProps>) {
  const headerRef = useRef<HTMLButtonElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  // Lazy mount: render the body only after the first expand, then keep it
  // mounted so collapsing doesn't lose in-progress form state.
  const [everExpanded, setEverExpanded] = useState(expanded);
  const [flashing, setFlashing] = useState(false);

  useEffect(() => {
    if (expanded) {
      setEverExpanded(true);
    }
  }, [expanded]);

  // APG focus return: if focus was inside the body when the section
  // collapsed, move it back to the header button.
  const prevExpanded = useRef(expanded);
  useEffect(() => {
    if (prevExpanded.current && !expanded) {
      const active = document.activeElement;
      if (active && bodyRef.current?.contains(active)) {
        headerRef.current?.focus();
      }
    }
    prevExpanded.current = expanded;
  }, [expanded]);

  useEffect(() => {
    if (flashKey > 0) {
      setFlashing(true);
      const id = setTimeout(() => setFlashing(false), 1400);
      return () => clearTimeout(id);
    }
  }, [flashKey]);

  const locked = state === 'locked';
  const badge = STATE_BADGE[state];

  return (
    <Card
      shadow="sm"
      padding="md"
      radius="md"
      id={`section-${slug}`}
      className={[
        classes.section,
        locked ? classes.sectionLocked : '',
        flashing ? classes.sectionFlash : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={`section-${slug}`}
    >
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <UnstyledButton
            ref={headerRef}
            onClick={() => !locked && onToggle(!expanded)}
            disabled={locked}
            aria-expanded={locked ? undefined : expanded}
            aria-controls={`section-body-${slug}`}
            style={{ flex: 1, cursor: locked ? 'default' : 'pointer' }}
            data-testid={`section-header-${slug}`}
          >
            <Group gap="xs" wrap="nowrap">
              {locked ? (
                <IconLock size={16} color="var(--mantine-color-gray-5)" />
              ) : expanded ? (
                <IconChevronDown size={16} />
              ) : (
                <IconChevronRight size={16} />
              )}
              <Text fw={600}>
                {index}. {label}
              </Text>
              {badge && (
                <Badge size="xs" variant="light" color={badge.color}>
                  {badge.label}
                </Badge>
              )}
            </Group>
          </UnstyledButton>
          {state === 'completed' && onChange && (
            <Button
              size="compact-xs"
              variant="subtle"
              onClick={onChange}
              data-testid={`section-change-${slug}`}
            >
              Change
            </Button>
          )}
        </Group>

        {locked && lockedHint && (
          <Text size="xs" c="dimmed" pl={26}>
            {lockedHint}
          </Text>
        )}

        {!locked && !expanded && summary && (
          <div style={{ paddingLeft: 26 }} data-testid={`section-summary-${slug}`}>
            {summary}
          </div>
        )}

        {!locked && (
          <Collapse in={expanded}>
            <div id={`section-body-${slug}`} ref={bodyRef}>
              {everExpanded ? children : null}
            </div>
          </Collapse>
        )}
      </Stack>
    </Card>
  );
}
